import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useThemeMode } from '../components/ThemeModeContext';
import Copyright from '../components/Copyright';
import './Start.css';
import AppStarLogo from '../images/appstar_title_tr.png';

// Sample prompts matching start.html
const SAMPLE_PROMPTS = [
  { title: 'Make a space invaders clone', desc: 'Keyboard controls, power-ups, escalating waves', prompt: 'Make a space invaders clone with keyboard controls and increasing difficulty levels.' },
  { title: 'Personal finance tracker', desc: 'Budgets, categories, monthly summaries', prompt: 'Create a personal finance tracker with budgets, categories, and monthly summaries.' },
  { title: 'Recipe finder', desc: 'Filter by ingredients and diet', prompt: 'Build a recipe finder that filters by ingredients and dietary preferences.' },
  { title: 'Habit tracker', desc: 'Streaks, reminders, insights', prompt: 'Develop a habit tracker with streaks, reminders, and weekly insights.' },
  { title: 'Real-time chat app', desc: 'Rooms, usernames, reactions', prompt: 'Design a real-time chat app with rooms, usernames, and message reactions.' },
  { title: 'Kanban task board', desc: 'Drag-and-drop, due dates', prompt: 'Create a task board like Kanban with drag-and-drop and due dates.' }
];

const FEATURE_CARDS = [
  { kicker: 'Personal Tools', title: 'Build tools just for yourself', desc: 'Describe what you need and AppStar helps you create practical tools for your own workflows.', icon: 'tools' },
  { kicker: 'Chat-First', title: 'Iterate naturally with AI', desc: 'Keep refining behavior, UI, and logic with conversational changes to make each tool fit how you work.', icon: 'chat' },
  { kicker: 'Build Anything', title: 'Create almost any kind of app', desc: 'You can make almost any kind of app from games, charts, animations, soundboards, drum machines, custom calculators, and lots more.', icon: 'spark' }
];

export default function Start() {
  const navigate = useNavigate();
  const { isDarkMode, clearOverride } = useThemeMode();
  const [value, setValue] = useState(localStorage.getItem('appstar_start_page_prompt') || '');
  const canvasRef = useRef(null);

  // Ensure Start initializes theme from OS preference by clearing any user override
  useEffect(() => {
    clearOverride();
  }, [clearOverride]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return undefined;
    }

    const context = canvas.getContext('2d');
    if (!context) {
      return undefined;
    }

    let stars = [];
    let width = window.innerWidth;
    let height = window.innerHeight;
    let mouseX = width / 2;
    let mouseY = height / 2;
    let frameId = null;

    const initStars = () => {
      const count = Math.max(200, Math.floor((width * height) / 1200));
      stars = Array.from({ length: count }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        z: Math.random(),
        seed: Math.random() * Math.PI * 2
      }));
    };

    const resize = () => {
      width = canvas.width = window.innerWidth;
      height = canvas.height = window.innerHeight;
      initStars();
    };

    const onMouseMove = (event) => {
      mouseX = event.clientX;
      mouseY = event.clientY;
    };

    const draw = (time) => {
      context.clearRect(0, 0, width, height);
      context.fillStyle = isDarkMode ? '#020617' : '#f8fafc';
      context.fillRect(0, 0, width, height);

      const centerX = width / 2;
      const centerY = height / 2;
      const now = time * 0.001;

      for (let index = 0; index < stars.length; index += 1) {
        const star = stars[index];
        const px = star.x + (mouseX - centerX) * (0.02 * (1 - star.z));
        const py = star.y + (mouseY - centerY) * (0.02 * (1 - star.z));
        const radius = (1 - star.z) * 1.2 + Math.sin(now * (0.6 + star.seed)) * 0.25;
        const alpha = (isDarkMode ? 0.35 : 0.18) + (isDarkMode ? 0.55 : 0.3) * Math.abs(Math.sin(now * 2 + star.seed));

        context.beginPath();
        context.arc(px, py, Math.max(0.2, radius), 0, Math.PI * 2);
        context.fillStyle = isDarkMode
          ? `rgba(255,255,255,${alpha})`
          : `rgba(51,65,85,${alpha})`;
        context.fill();
      }
    };

    const loop = (timestamp) => {
      draw(timestamp);
      frameId = window.requestAnimationFrame(loop);
    };

    resize();
    window.addEventListener('resize', resize);
    window.addEventListener('mousemove', onMouseMove);
    frameId = window.requestAnimationFrame(loop);

    return () => {
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', onMouseMove);
      if (frameId) {
        window.cancelAnimationFrame(frameId);
      }
    };
  }, [isDarkMode]);

  const fillPrompt = useCallback((text) => {
    setValue(text);
  }, []);

  const handleSend = useCallback(() => {
    const prompt = value.trim();
    if (prompt) {
      localStorage.setItem('appstar_start_page_prompt', prompt);
    }
    navigate('/signup');
  }, [value, navigate]);

  const handleKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSend();
      return;
    }
    if (!e.shiftKey && !e.metaKey && !e.ctrlKey && e.key === 'Enter') {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className={`start-page ${isDarkMode ? 'dark' : 'light'}`}>
      <canvas ref={canvasRef} className="start-stars" aria-label="starry background" />
      <div className="start-bg-glow a" />
      <div className="start-bg-glow b" />

      <div className="start-content">
        <div className="start-auth-links">
          <Link to="/login" className="start-login-link">Login</Link>
          <Link to="/signup" className="start-signup-link">Signup</Link>
        </div>

        <header className="start-header">
          <img src={AppStarLogo} alt="AppStar Logo" className="start-logo" />
          <h1 className="start-title">
            <span className="gradient-title">Welcome to AppStar</span>
          </h1>
          <p className="start-subtitle">
            Prototype and Build Your Own AI Tools — Just by Describing Them
          </p>
        </header>

        <main className="start-main">
          <section className="start-pills" aria-label="Core value highlights">
            <div className="start-pill">Make apps for yourself in minutes</div>
            <div className="start-pill">Prompt-to-prototype in minutes</div>
            <div className="start-pill">Get answers and visualizations fast</div>
          </section>

          <section className="start-panel start-composer">
            <label htmlFor="prompt" style={{ position: 'absolute', left: -9999 }}>Your app idea</label>
            <div className="start-send-wrap">
              <textarea
                id="prompt"
                rows={4}
                placeholder="Describe the app you want to build..."
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={handleKeyDown}
              />

              <button type="button" onClick={handleSend} className="start-send-btn">
                <span>Send</span>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ width: 20, height: 20 }}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12l-7.5 7.5M21 12H3" />
                </svg>
              </button>
            </div>
            <p className="start-tip">Tip: Press Enter (Cmd/Ctrl+Enter on desktop) to send.</p>
          </section>

          <section className="start-section">
            <h2>What you can build</h2>
            <div className="start-feature-cards">
              {FEATURE_CARDS.map((feature) => (
                <article key={feature.title} className="start-feature-card">
                  <div className="start-feature-icon" aria-hidden="true">
                    {feature.icon === 'tools' && (
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M14.7 6.3a4.5 4.5 0 0 0-6.18 5.95L4.5 16.27a2.121 2.121 0 1 0 3 3l4.02-4.02a4.5 4.5 0 0 0 5.95-6.18l-3.14 3.14-2.12-2.12 2.49-3.79Z" />
                      </svg>
                    )}
                    {feature.icon === 'chat' && (
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M8 10h8M8 14h5m-9 5 2.4-3.2A9 9 0 1 1 21 12a9 9 0 0 1-9 9 8.96 8.96 0 0 1-4.6-1.2L4 19Z" />
                      </svg>
                    )}
                    {feature.icon === 'spark' && (
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7">
                        <path strokeLinecap="round" strokeLinejoin="round" d="m12 3 1.9 3.86L18 8.8l-3 2.92.71 4.13L12 13.86 8.29 15.85 9 11.72 6 8.8l4.1-1.94L12 3Z" />
                      </svg>
                    )}
                  </div>
                  <p className="start-feature-kicker">{feature.kicker}</p>
                  <h3>{feature.title}</h3>
                  <p>{feature.desc}</p>
                </article>
              ))}
            </div>
          </section>

          <section className="start-section" style={{ marginTop: '1.7rem' }}>
            <h2>Try a sample prompt</h2>
            <div className="start-cards">
              {SAMPLE_PROMPTS.map((card) => (
                <button
                  key={card.title}
                  type="button"
                  onClick={() => fillPrompt(card.prompt)}
                  className="start-card"
                >
                  <p className="start-card-title">{card.title}</p>
                  <p className="start-card-desc">{card.desc}</p>
                </button>
              ))}
            </div>
          </section>
        </main>

        <footer className="start-footer">
          <Copyright sx={{ mt: 0, mb: 0 }} />
        </footer>
      </div>
    </div>
  );
}
